import {html, css, LitElement, nothing} from 'lit';
import {OrderableBehavior} from '../behaviors/orderable-behavior.js';
import {HotkeyBehavior} from '../behaviors/hotkey-behavior.js';
import {OutsideClickBehavior} from '../behaviors/outside-click-behavior';

class KeliBrowseConfigurationMenu extends OutsideClickBehavior(HotkeyBehavior(OrderableBehavior(LitElement))) {
  static styles = css`
      :host {
          transform: scale(0);
          position: absolute;
          display: block;
          box-sizing: border-box;
          min-height: 150px;
          width: 300px;
          padding: 16px 0;
          overflow-y: auto;
          cursor: default;
          color: var(--keli-text);
          background-color: var(--keli-background-color);
          box-shadow: var(--keli-shadow-z1);
          border: var(--keli-container-border);
          transition: all var(--keli-transition-fast);
      }

      :host(.open) {
          transform: scale(1);
      }

      #filter {
          padding: 7px 24px 7px 24px;
      }

      #reset {
          display: block;
          text-decoration: underline;
          cursor: pointer;
          color: var(--keli-a-color);
          white-space: nowrap;
          text-align: center;
      }

      #search {
          display: block;
          background-color: var(--keli-subtle-background);
          border-radius: 25px;
          border: none;
          font-family: inherit;
          font-size: 1em;
          outline: none;
          padding: 5px 15px;
          width: 100%;
          box-sizing: border-box;
      }

      .hidden {
          display: none;
      }

      .orderable-ghost {
          cursor: grabbing;
          background-color: var(--keli-subtle-background);
      }
  `;

  static properties = {
    columns: {type: Array},
    opened: {type: Boolean},
    browse: {type: Object},
    fromElement: {type: Object},
    fromElementPosition: {type: Object},
  };

  constructor() {
    super();
    this.columns = [];
  }

  render() {
    return html`
        <div id="filter">
            <span id="reset" @click="${this.reset}">Reset to Default</span>
            <input type="search" id="search" @input="${this.search}" autocomplete="off" placeholder="Filter">
        </div>

        <div id="items">
            ${this.opened && this.columns?.length ? this.columns.map(item => html`
                <keli-menu-item keliname="${item.keliname}"
                                caption="${item.caption ?? nothing}"
                                checked="${(item.checked ? '' : nothing)}"
                                show-toggle
                                show-handle
                                @keli-menu-item-toggled="${this.handleItemToggled}"></keli-menu-item>`) : nothing}
        </div>`;
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    for (const property of changedProperties.keys()) {
      if (this[`${property}Changed`]) {
        this[`${property}Changed`](this[property]);
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('orderable-end', this.handleOrderableEnd);
    this.addEventListener('outside-click', this.handleOutsideClick);

    this.registerHotkey(KeliInterface.Hotkeys.browse.menu.close, {
      down: () => {
        this.opened = false;
      }
    });
  }

  firstUpdated() {
    this.orderableSelector = 'keli-menu-item';
    this.orderableHandle = '#handle';
    this.orderableAnimation = 0;
    this.orderableElement = this.renderRoot.querySelector('#items');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('orderable-end', this.handleOrderableEnd);
    this.removeEventListener('outside-click', this.handleOutsideClick);

    this.browse = null;
    this.fromElement = null;
    this.columns = null;
  }

  handleOrderableEnd(event) {
    if (typeof event.detail.newIndex === 'undefined' || event.detail.newIndex === event.detail.oldIndex) {
      return;
    }
    if (event.detail.newIndex >= this.columns.length) {
      let k = event.detail.newIndex - this.columns.length;
      while ((k--) + 1) {
        this.columns.push(undefined);
      }
    }
    this.columns.splice(event.detail.newIndex, 0, this.columns.splice(event.detail.oldIndex, 1)[0]);

    this.fromElement.dispatchEvent(new CustomEvent('menuSortableReordered', {
      bubbles: true,
      composed: true,
      detail: {element: this}
    }));
  }

  handleItemToggled(event) {
    const target = getEventTarget(event);
    const control = this.columns.find(item => item.keliname === target.keliname);
    if (control) {
      control.checked = target.checked;
    }

    this.fromElement.dispatchEvent(new CustomEvent('menuItemToggled', {
      bubbles: true,
      composed: true,
      detail: event.detail
    }));
  }

  handleOutsideClick() {
    this.opened = false;
  }

  openedChanged(newValue) {
    const searchElement = this.renderRoot.querySelector('#search');
    this.classList.toggle('open', newValue);
    if (newValue) {
      searchElement.focus();
      this.outsideClickElements = [this.fromElement];
      this.outsideClickListening = true;
    } else {
      searchElement.value = '';
      this.search();
      this.browse.dispatchEvent(new CustomEvent('focusElement'));
      this.outsideClickListening = false;
    }
  }

  fromElementPositionChanged(newValue) {
    this.style.top = this.style.right = this.style.bottom = this.style.left = 'auto';
    const positions = [];
    for (let position in newValue) {
      if (!newValue.hasOwnProperty(position)) {
        continue;
      }
      if (newValue[position] === true) {
        positions.push(position);
        this.style[position] = 0;
      }
    }
    this.style.transformOrigin = this.style.webkitTransformOrigin = positions.join(' ');
  }

  reset() {
    this.dispatchEvent(new CustomEvent('keliBrowseConfigurationMenuReset', {bubbles: true, composed: true}));
    this.opened = false;
  }

  search() {
    const newValue = this.renderRoot.querySelector('#search').value;

    // This has to be done every time because captions can be dynamic
    if (!this.matchizzle) {
      this.matchizzle = new Matchizzle([], {keys: ['text'], id: 'element'});
    }
    let items = this.renderRoot.querySelector('#items').querySelectorAll('keli-menu-item');
    this.matchizzle.list = [];
    for (let item of items) {
      this.matchizzle.list.push({
        element: item,
        text: item.caption
      });
    }

    const visibleElements = this.matchizzle.search(newValue);
    items.forEach((item) => {
      if (visibleElements.includes(item)) {
        item.classList.remove('hidden');
      } else {
        item.classList.add('hidden');
      }
    });
  }
}

customElements.define('keli-browse-configuration-menu', KeliBrowseConfigurationMenu);